import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import cx from 'classnames'
import styles from './GroupTicketingCard.module.scss'
import { Col, Row } from 'react-grid-system'
import { Button, Heading, Paragraph } from '~elements'
import { GroupTicketingCardProps } from '~types'
import { getGtmLinkPosition } from '~analyticsUtils'
import { useWindowSize } from 'utils/hooks'
import { getBackgroundGradientStyle } from 'utils'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { getImageUrl } from 'utils/assets/getImageUrl'

const GroupTicketingCard = ({
  media,
  headline,
  eyebrow,
  eyebrowColor,
  description,
  backgroundGradient,
  backgroundColor,
  buttonGroup,
  hasMediaInRightColumn,
  groupTicketingCardId,
  heading,
  headlineIsCenterAligned = false,
  eyebrowIsCenterAligned = false,
  descriptionIsCenterAligned = false,
  buttonGroupIsCenterAligned = false
}: GroupTicketingCardProps) => {
  const groupTicketingCardStyles = cx([
    styles['group-ticketing'],
    styles[`has-media-in-${hasMediaInRightColumn ? 'right' : 'left'}-col`]
  ])
  const contentWrapperStyles = cx([
    styles['col-wrapper--content'],
    styles[headlineIsCenterAligned && eyebrowIsCenterAligned && descriptionIsCenterAligned ? 'center-aligned' : '']
  ])
  const buttonContainerStyles = cx([
    styles['btn-container'],
    styles[buttonGroupIsCenterAligned ? 'center-aligned' : '']
  ])
  const isImage = media && media.componentName === 'Image'
  const hasVideo = media && media.video
  const videoUrl = hasVideo && media.video[0]?.url ? media.video[0].url : null
  const imageUrl = isImage ? getImageUrl({ imageUrl: media?.image?.url }) : null
  const posterImageUrl = hasVideo ? getImageUrl({ imageUrl: media?.posterImage[0]?.url }) : null
  const { isBreakpoint: isBelowBreakpointMd } = useWindowSize('md', '<')
  const [backgroundStyle, setBackgroundStyle] = useState(() =>
    getBackgroundGradientStyle(backgroundGradient, backgroundColor?.hex, isBelowBreakpointMd, hasMediaInRightColumn)
  )
  const linkModule = 'card'

  useEffect(() => {
    const newStyle = getBackgroundGradientStyle(
      backgroundGradient,
      backgroundColor?.hex,
      isBelowBreakpointMd,
      hasMediaInRightColumn
    )
    setBackgroundStyle(newStyle)
  }, [isBelowBreakpointMd, backgroundGradient, backgroundColor])

  return (
    <div className={styles['container']} id={groupTicketingCardId}>
      {heading ? (
        <Heading level={2} justify='center'>
          {heading}
        </Heading>
      ) : null}
      <Row
        className={groupTicketingCardStyles}
        nogutter={true}
        direction={hasMediaInRightColumn ? 'row-reverse' : 'row'}
      >
        <Col md={6}>
          <div className={`${styles['col-wrapper--media']} ${hasMediaInRightColumn ? styles['has-media-right'] : ''}`}>
            {media && (
              <div className={styles['media-column']} key={media.id}>
                <div className={styles['media-overlay']} />

                {isImage && imageUrl ? (
                  <Image
                    src={imageUrl}
                    alt={media.alt ?? ''}
                    style={{ objectFit: 'cover' }}
                    fill
                    quality={NEXT_IMAGE_QUALITY}
                  />
                ) : videoUrl ? (
                  <div className={styles['video-container']}>
                    <video
                      id='background-video'
                      loop
                      muted
                      autoPlay={true}
                      width={'100%'}
                      poster={posterImageUrl ?? ''}
                      disableRemotePlayback
                      controls={false}
                      playsInline={true}
                    >
                      <source src={videoUrl} type='video/mp4' />
                    </video>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </Col>
        <Col md={6} className='align-contents-vertical'>
          <div className={`${styles['gradient-block']}`} style={{ background: backgroundStyle }}>
            <div className={contentWrapperStyles} id={groupTicketingCardId}>
              <div className={styles['text-content']}>
                {headline && (
                  <Heading level={4} justify={headlineIsCenterAligned ? 'center' : 'left'}>
                    <>{headline}</>
                  </Heading>
                )}
                {eyebrow && (
                  <Heading
                    level={6}
                    levelDisplay={'eyebrow'}
                    color={eyebrowColor}
                    justify={eyebrowIsCenterAligned ? 'center' : 'left'}
                  >
                    {eyebrow}
                  </Heading>
                )}
                {description?.html && (
                  <Paragraph text={description} justify={descriptionIsCenterAligned ? 'center' : 'left'} />
                )}
              </div>
              {buttonGroup && buttonGroup.length > 0 && (
                <div className={buttonContainerStyles}>
                  {buttonGroup.map((button, index) => (
                    <Button
                      key={`${button.title}_${index}`}
                      {...button}
                      analytics={{
                        clickTracking: `${linkModule}:${headline}:${button.title}:cta`,
                        linkPosition: getGtmLinkPosition(buttonGroup, index),
                        linkModule: linkModule
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default GroupTicketingCard
